import React, { useEffect, useState } from 'react';
import { Table, Button, Space, Tag, Modal, Form, Select, Input, Upload, message, Popconfirm, Card, Progress } from 'antd';
import { PlusOutlined, UploadOutlined, ArrowLeftOutlined, CopyOutlined, DeleteOutlined } from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import { versionService, Version } from '../services/version';
import { uploadFile } from '../services/upload';
import { applicationService, Application } from '../services/application';
import dayjs from 'dayjs';

const Versions: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<Version[]>([]);
  const [app, setApp] = useState<Application | null>(null);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState<any[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  useEffect(() => {
    if (id) {
      loadApp();
      loadData();
    }
  }, [id]);

  const loadApp = async () => {
    try {
      const response: any = await applicationService.getOne(Number(id));
      setApp(response);
    } catch (error) {
      message.error('加载应用信息失败');
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const response: any = await versionService.getByApplication(Number(id));
      setData(response);
    } catch (error) {
      message.error('加载失败');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    form.resetFields();
    setFileList([]);
    setModalVisible(true);
  };

  const handleSubmit = async (values: any) => {
    try {
      setUploading(true);
      setUploadProgress(0);

      const formData: Record<string, any> = {
        versionNumber: values.versionNumber,
        status: values.status,
        updateType: values.updateType || 'Download',
      };
      
      if (values.changelog) {
        formData.changelog = values.changelog;
      }

      if (values.updateType === 'URL') {
        // URL 更新方式
        if (!values.updateUrl) {
          message.error('请输入更新地址');
          setUploading(false);
          return;
        }
        formData.updateUrl = values.updateUrl;
        
        // URL 方式不需要上传文件，直接提交
        const submitFormData = new FormData();
        Object.keys(formData).forEach(key => {
          submitFormData.append(key, formData[key]);
        });
        await versionService.create(Number(id), submitFormData);
      } else {
        // 下载更新方式
        if (fileList.length === 0) {
          message.error('请上传版本文件');
          setUploading(false);
          return;
        }

        const file = fileList[0].originFileObj || fileList[0];
        if (!file) {
          message.error('文件对象无效，请重新选择文件');
          setUploading(false);
          return;
        }

        // 使用智能上传（支持断点续传）
        await uploadFile(file, Number(id), formData, (progress) => {
          setUploadProgress(progress.percent);
        });
      }

      message.success('发布成功');
      setModalVisible(false);
      setFileList([]);
      setUploadProgress(0);
      loadData();
    } catch (error: any) {
      console.error('发布错误:', error);
      message.error(error.response?.data?.message || '发布失败');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (versionId: number) => {
    try {
      await versionService.delete(versionId);
      message.success('删除成功');
      loadData();
    } catch (error) {
      message.error('删除失败');
    }
  };

  // 获取文件扩展名
  const getFileExtension = (fileName: string): string => {
    if (!fileName) return '';
    const lastDot = fileName.lastIndexOf('.');
    return lastDot >= 0 ? fileName.substring(lastDot) : '';
  };

  // 构建下载文件名：包名_版本号.扩展名
  const buildDownloadFileName = (identifier: string, versionNumber: string, originalFileName: string): string => {
    const ext = getFileExtension(originalFileName);
    return `${identifier}_${versionNumber}${ext}`;
  };

  const handleCopyLink = (record: Version) => {
    let link = '';
    if (record.updateType === 'URL' && record.updateUrl) {
      link = record.updateUrl;
    } else if (record.fileName && app) {
      // 构建带文件名的下载链接
      const downloadFileName = buildDownloadFileName(app.identifier, record.versionNumber, record.fileName);
      link = `${window.location.origin}/api/client/download/${record.id}/${encodeURIComponent(downloadFileName)}`;
    }
    
    if (link) {
      navigator.clipboard.writeText(link).then(() => {
        message.success('下载链接已复制到剪贴板');
      }).catch(() => {
        message.error('复制失败，请手动复制');
      });
    }
  };

  const columns = [
    {
      title: '版本号',
      dataIndex: 'versionNumber',
      key: 'version',
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const statusMap: any = {
          Production: { color: 'green', text: 'Production（正式版）' },
          Beta: { color: 'blue', text: 'Beta（测试版）' },
          Alpha: { color: 'orange', text: 'Alpha（内测版）' },
          Deprecated: { color: 'red', text: 'Deprecated（已废弃）' },
        };
        const config = statusMap[status] || { color: 'default', text: status };
        return <Tag color={config.color}>{config.text}</Tag>;
      },
    },
    {
      title: '更新方式',
      dataIndex: 'updateType',
      key: 'updateType',
      render: (type: string) => {
        const typeMap: any = {
          Download: { color: 'blue', text: '程序下载' },
          URL: { color: 'purple', text: 'URL跳转' },
        };
        const config = typeMap[type] || { color: 'default', text: type || '程序下载' };
        return <Tag color={config.color}>{config.text}</Tag>;
      },
    },
    {
      title: '日期',
      dataIndex: 'createdAt',
      key: 'date',
      render: (date: string) => dayjs(date).format('YYYY-MM-DD'),
    },
    {
      title: '文件 / 更新日志',
      key: 'info',
      render: (record: Version) => (
        <div>
          {record.fileName && (
            <div>
              <strong>{record.fileName}</strong>
              {record.fileSize && (
                <span style={{ color: '#999', marginLeft: 8 }}>
                  ({(record.fileSize / 1024 / 1024).toFixed(2)} MB)
                </span>
              )}
            </div>
          )}
          {record.changelog && (
            <div style={{ color: '#666', marginTop: 4 }}>{record.changelog}</div>
          )}
        </div>
      ),
    },
    {
      title: '下载次数',
      dataIndex: 'downloadCount',
      key: 'downloads',
    },
    {
      title: '发布人',
      dataIndex: 'createdByName',
      key: 'createdByName',
      render: (name: string) => name || '-',
    },
    {
      title: '操作',
      key: 'action',
      render: (_: any, record: Version) => (
        <Space>
          {(record.fileName || record.updateUrl) && (
            <Button
              type="link"
              icon={<CopyOutlined />}
              onClick={() => handleCopyLink(record)}
            >
              复制链接
            </Button>
          )}
          <Popconfirm
            title="确定删除此版本吗？"
            onConfirm={() => handleDelete(record.id)}
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Button
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate('/applications')}
        style={{ marginBottom: 16 }}
      >
        返回
      </Button>

      {app && (
        <Card style={{ marginBottom: 16 }}>
          <h2>{app.name}</h2>
          <p style={{ color: '#666', margin: 0 }}>
            {app.identifier} • <Tag>{app.platform}</Tag> • <Tag color={app.status === 'Active' ? 'green' : 'blue'}>{app.status}</Tag>
          </p>
        </Card>
      )}

      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
        <h3>版本历史</h3>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
          发布新版本
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={data}
        loading={loading}
        rowKey="id"
      />

      <Modal
        title="发布新版本"
        open={modalVisible}
        onCancel={() => !uploading && setModalVisible(false)}
        onOk={() => form.submit()}
        confirmLoading={uploading}
        width={600}
        maskClosable={!uploading}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="versionNumber"
            label="版本号"
            rules={[{ required: true, message: '请输入版本号' }]}
          >
            <Input placeholder="例如: 1.0.0" />
          </Form.Item>

          <Form.Item
            name="status"
            label="状态"
            rules={[{ required: true, message: '请选择状态' }]}
          >
            <Select placeholder="选择版本状态">
              <Select.Option value="Production">Production（正式版）</Select.Option>
              <Select.Option value="Beta">Beta（测试版）</Select.Option>
              <Select.Option value="Alpha">Alpha（内测版）</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item name="changelog" label="更新日志">
            <Input.TextArea rows={4} placeholder="描述此版本的更新内容..." />
          </Form.Item>

          <Form.Item
            name="updateType"
            label="更新方式"
            rules={[{ required: true, message: '请选择更新方式' }]}
            initialValue="Download"
          >
            <Select placeholder="选择更新方式" onChange={(value) => {
              if (value === 'URL') {
                setFileList([]);
              }
            }}>
              <Select.Option value="Download">程序下载更新</Select.Option>
              <Select.Option value="URL">URL 跳转更新</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => prevValues.updateType !== currentValues.updateType}
          >
            {({ getFieldValue }) =>
              getFieldValue('updateType') === 'URL' ? (
                <Form.Item
                  name="updateUrl"
                  label="更新地址"
                  rules={[
                    { required: true, message: '请输入更新地址' },
                    { type: 'url', message: '请输入有效的 URL' }
                  ]}
                >
                  <Input placeholder="例如: https://example.com/download" />
                </Form.Item>
              ) : (
                <Form.Item 
                  label="上传文件" 
                  required
                  tooltip="必须上传版本文件"
                >
                  <Upload
                    fileList={fileList}
                    beforeUpload={(file) => {
                      setFileList([file]);
                      return false;
                    }}
                    onRemove={() => setFileList([])}
                    maxCount={1}
                  >
                    <Button icon={<UploadOutlined />}>选择文件（必填）</Button>
                  </Upload>
                  {fileList.length === 0 && (
                    <div style={{ color: '#ff4d4f', marginTop: 8 }}>
                      请选择要上传的版本文件
                    </div>
                  )}
                </Form.Item>
              )
            }
          </Form.Item>

          {uploading && uploadProgress > 0 && (
            <Form.Item label="上传进度">
              <Progress percent={uploadProgress} status="active" />
            </Form.Item>
          )}
        </Form>
      </Modal>
    </div>
  );
};

export default Versions;
